import { queryGpuInstant, queryGpuRange } from '@/core/observability_api_client'
import type { PromQLResponse, PromQLResult } from '@/core/observability_types'
import { GPU_TELEMETRY_ENVIRONMENTS, type GpuEnvironment } from './gpu_environment'

const GPU_ENVIRONMENT_SET = new Set<string>(GPU_TELEMETRY_ENVIRONMENTS)
// NVIDIA classifies these aliases as power-limit clock optimization, not a
// hardware fault. Keep raw gauges intact and never downgrade a failed watch.
const POWER_LIMIT_CLOCK_EVENT_CODES = new Set([
  'DCGM_FR_CLOCK_THROTTLE_POWER',
  'DCGM_FR_CLOCKS_EVENT_POWER',
])

export const GPU_CAPABILITIES = ['embeddings', 'rerank', 'extraction', 'pii_spans'] as const
export type GpuCapability = (typeof GPU_CAPABILITIES)[number]

export function gpuInstantQueries(environment: GpuEnvironment) {
  return {
    targets: `up{job=~"gpu-model-backend|gpu-dcgm",env="${environment}"}`,
    freshness: `min by (env, instance_id) (timestamp(agion_gpu_model_ready{env="${environment}"}) or timestamp(DCGM_FI_DEV_FB_FREE{env="${environment}"}))`,
    gauges: `{__name__=~"agion_gpu_model_ready|DCGM_FI_DEV_FB_FREE|DCGM_FI_DEV_FB_USED|DCGM_FI_DEV_FB_RESERVED|DCGM_FI_DEV_GPU_TEMP|DCGM_FI_DEV_POWER_USAGE|DCGM_FI_DEV_GPU_UTIL|DCGM_FI_DEV_MEM_COPY_UTIL|DCGM_EXP_GPU_HEALTH_STATUS",env="${environment}"}`,
    rejections: `sum by (env, instance_id) (increase(agion_gpu_model_rejected_total{env="${environment}"}[10m]))`,
    recoveries: `label_replace(sum by (env, instance_id) (changes(agion_gpu_model_process_start_time_seconds{env="${environment}"}[1h])), "recovery_kind", "process", "", "") or label_replace(sum by (env, instance_id) (increase(agion_gpu_model_session_resets_total{env="${environment}"}[1h])), "recovery_kind", "session", "", "")`,
    capabilityThroughput: `sum by (env, instance_id, capability) (rate(agion_gpu_model_capability_success_total{env="${environment}"}[5m]))`,
    capabilityFailures: `sum by (env, instance_id, capability) (increase(agion_gpu_model_capability_failure_total{env="${environment}"}[10m]))`,
    capabilityLatency: `max by (env, instance_id, capability) (agion_gpu_model_capability_last_latency_seconds{env="${environment}"})`,
    alerts: `ALERTS{alertstate="firing",alertname=~"gpu_.*",env="${environment}"}`,
  } as const
}

export function gpuRangeQueries(environment: GpuEnvironment) {
  return {
    framebuffer: `min by (env, instance_id) (DCGM_FI_DEV_FB_FREE{env="${environment}"})`,
    throughput: `sum by (env, instance_id) (rate(agion_gpu_model_capability_success_total{env="${environment}"}[5m]))`,
  } as const
}

type InstantQueryKey = keyof ReturnType<typeof gpuInstantQueries>
type RangeQueryKey = keyof ReturnType<typeof gpuRangeQueries>

export interface GpuCapabilityMetrics {
  throughput: number | null
  failures: number | null
  latencySeconds: number | null
}

export interface GpuAlert {
  name: string
  severity: string
}

export interface GpuSeriesPoint {
  time: number
  value: number
}

export interface GpuEnvironmentMetrics {
  environment: GpuEnvironment
  instanceId: string | null
  backendUp: boolean | null
  dcgmUp: boolean | null
  ready: boolean | null
  framebufferFreeMiB: number | null
  framebufferUsedMiB: number | null
  framebufferReservedMiB: number | null
  gpuUtilizationPct: number | null
  memoryUtilizationPct: number | null
  temperatureCelsius: number | null
  powerWatts: number | null
  hardwareHealth: number | null
  throughput: number | null
  failures: number | null
  rejections: number | null
  recoveries: number | null
  sessionResets: number | null
  capabilities: Record<GpuCapability, GpuCapabilityMetrics>
  alerts: GpuAlert[]
  framebufferSeries: GpuSeriesPoint[]
  throughputSeries: GpuSeriesPoint[]
  lastSampleUnix: number | null
}

export interface GpuDashboardSnapshot {
  metrics: GpuEnvironmentMetrics
  refreshedAt: number
}

export interface GpuDashboardLoadResult {
  snapshot: GpuDashboardSnapshot
  failedQueries: string[]
}

function emptyCapabilityMetrics(): GpuCapabilityMetrics {
  return { throughput: null, failures: null, latencySeconds: null }
}

function emptyCapabilities(): Record<GpuCapability, GpuCapabilityMetrics> {
  return Object.fromEntries(
    GPU_CAPABILITIES.map(capability => [capability, emptyCapabilityMetrics()]),
  ) as Record<GpuCapability, GpuCapabilityMetrics>
}

function emptyEnvironment(environment: GpuEnvironment): GpuEnvironmentMetrics {
  return {
    environment,
    instanceId: null,
    backendUp: null,
    dcgmUp: null,
    ready: null,
    framebufferFreeMiB: null,
    framebufferUsedMiB: null,
    framebufferReservedMiB: null,
    gpuUtilizationPct: null,
    memoryUtilizationPct: null,
    temperatureCelsius: null,
    powerWatts: null,
    hardwareHealth: null,
    throughput: null,
    failures: null,
    rejections: null,
    recoveries: null,
    sessionResets: null,
    capabilities: emptyCapabilities(),
    alerts: [],
    framebufferSeries: [],
    throughputSeries: [],
    lastSampleUnix: null,
  }
}

export function createEmptyGpuDashboard(environment: GpuEnvironment): GpuDashboardSnapshot {
  return {
    metrics: emptyEnvironment(environment),
    refreshedAt: 0,
  }
}

function cloneDashboard(environment: GpuEnvironment, previous?: GpuDashboardSnapshot): GpuDashboardSnapshot {
  if (!previous || previous.metrics.environment !== environment) return createEmptyGpuDashboard(environment)
  return structuredClone(previous)
}

function environmentOf(result: PromQLResult): GpuEnvironment | null {
  const environment = result.metric['env']
  return environment && GPU_ENVIRONMENT_SET.has(environment) ? environment as GpuEnvironment : null
}

function capabilityOf(result: PromQLResult): GpuCapability | null {
  const capability = result.metric['capability']
  return GPU_CAPABILITIES.find(candidate => candidate === capability) ?? null
}

function vectorValue(result: PromQLResult): number | null {
  const parsed = result.value ? Number.parseFloat(result.value[1]) : Number.NaN
  return Number.isFinite(parsed) ? parsed : null
}

function updateIdentity(metrics: GpuEnvironmentMetrics, result: PromQLResult): void {
  metrics.instanceId = result.metric['instance_id'] || metrics.instanceId
}

function successfulResults(response: PromQLResponse): PromQLResult[] {
  if (response.status !== 'success') {
    throw new Error(response.error || response.errorType || 'observability query failed')
  }
  return response.data?.result ?? []
}

function applyTargets(snapshot: GpuDashboardSnapshot, response: PromQLResponse): void {
  snapshot.metrics.backendUp = null
  snapshot.metrics.dcgmUp = null
  for (const result of successfulResults(response)) {
    const environment = environmentOf(result)
    const value = vectorValue(result)
    if (environment !== snapshot.metrics.environment || value === null) continue
    const metrics = snapshot.metrics
    updateIdentity(metrics, result)
    if (result.metric['job'] === 'gpu-model-backend') metrics.backendUp = value === 1
    if (result.metric['job'] === 'gpu-dcgm') metrics.dcgmUp = value === 1
  }
}

function applyFreshness(snapshot: GpuDashboardSnapshot, response: PromQLResponse): void {
  snapshot.metrics.lastSampleUnix = null
  for (const result of successfulResults(response)) {
    const environment = environmentOf(result)
    const sourceSampleTimestamp = vectorValue(result)
    if (environment !== snapshot.metrics.environment || sourceSampleTimestamp === null) continue
    const metrics = snapshot.metrics
    updateIdentity(metrics, result)
    metrics.lastSampleUnix = sourceSampleTimestamp
  }
}

function applyGauges(snapshot: GpuDashboardSnapshot, response: PromQLResponse): void {
  type FramebufferSample = {
    environment: GpuEnvironment
    freeMiB: number | null
    usedMiB: number | null
    reservedMiB: number | null
  }
  const framebufferByDevice = new Map<string, FramebufferSample>()
  const maxima = new Map<string, number>()
  for (const result of successfulResults(response)) {
    const environment = environmentOf(result)
    const value = vectorValue(result)
    const name = result.metric['__name__']
    if (environment !== snapshot.metrics.environment || value === null || !name) continue
    const metrics = snapshot.metrics
    updateIdentity(metrics, result)
    const key = `${environment}:${name}`
    if (name === 'DCGM_FI_DEV_FB_FREE' || name === 'DCGM_FI_DEV_FB_USED' || name === 'DCGM_FI_DEV_FB_RESERVED') {
      const device = result.metric['UUID'] || result.metric['gpu'] || 'default'
      const deviceKey = `${environment}:${result.metric['instance_id'] ?? ''}:${device}`
      const sample = framebufferByDevice.get(deviceKey) ?? {
        environment,
        freeMiB: null,
        usedMiB: null,
        reservedMiB: null,
      }
      if (name === 'DCGM_FI_DEV_FB_FREE') sample.freeMiB = value
      if (name === 'DCGM_FI_DEV_FB_USED') sample.usedMiB = value
      if (name === 'DCGM_FI_DEV_FB_RESERVED') sample.reservedMiB = value
      framebufferByDevice.set(deviceKey, sample)
    } else {
      const hardwareValue = name === 'DCGM_EXP_GPU_HEALTH_STATUS'
        && value === 10
        && POWER_LIMIT_CLOCK_EVENT_CODES.has(result.metric['health_error_code'] ?? '')
        ? 0 : value
      maxima.set(key, Math.max(maxima.get(key) ?? Number.NEGATIVE_INFINITY, hardwareValue))
    }
  }

  const environment = snapshot.metrics.environment
  const metrics = snapshot.metrics
  const readMax = (name: string) => maxima.get(`${environment}:${name}`) ?? null
  const worstFramebuffer = [...framebufferByDevice.values()]
    .filter(sample => sample.environment === environment && sample.freeMiB !== null)
    .reduce<FramebufferSample | null>((worst, sample) => (
      worst === null || (sample.freeMiB ?? Number.POSITIVE_INFINITY) < (worst.freeMiB ?? Number.POSITIVE_INFINITY)
        ? sample
        : worst
    ), null)
  metrics.ready = readMax('agion_gpu_model_ready') === null ? null : readMax('agion_gpu_model_ready') === 1
  metrics.framebufferFreeMiB = worstFramebuffer?.freeMiB ?? null
  metrics.framebufferUsedMiB = worstFramebuffer?.usedMiB ?? null
  metrics.framebufferReservedMiB = worstFramebuffer?.reservedMiB ?? null
  metrics.gpuUtilizationPct = readMax('DCGM_FI_DEV_GPU_UTIL')
  metrics.memoryUtilizationPct = readMax('DCGM_FI_DEV_MEM_COPY_UTIL')
  metrics.temperatureCelsius = readMax('DCGM_FI_DEV_GPU_TEMP')
  metrics.powerWatts = readMax('DCGM_FI_DEV_POWER_USAGE')
  metrics.hardwareHealth = readMax('DCGM_EXP_GPU_HEALTH_STATUS')
}

function applyRejections(snapshot: GpuDashboardSnapshot, response: PromQLResponse): void {
  snapshot.metrics.rejections = null
  for (const result of successfulResults(response)) {
    const environment = environmentOf(result)
    const value = vectorValue(result)
    if (environment !== snapshot.metrics.environment || value === null) continue
    const metrics = snapshot.metrics
    updateIdentity(metrics, result)
    metrics.rejections = value
  }
}

function applyRecoveries(snapshot: GpuDashboardSnapshot, response: PromQLResponse): void {
  snapshot.metrics.recoveries = null
  snapshot.metrics.sessionResets = null
  for (const result of successfulResults(response)) {
    const environment = environmentOf(result)
    const value = vectorValue(result)
    if (environment !== snapshot.metrics.environment || value === null) continue
    const metrics = snapshot.metrics
    updateIdentity(metrics, result)
    if (result.metric['recovery_kind'] === 'process') metrics.recoveries = value
    if (result.metric['recovery_kind'] === 'session') metrics.sessionResets = value
  }
}

function applyCapability(
  snapshot: GpuDashboardSnapshot,
  response: PromQLResponse,
  field: keyof GpuCapabilityMetrics,
): void {
  for (const capability of GPU_CAPABILITIES) {
    snapshot.metrics.capabilities[capability][field] = null
  }
  for (const result of successfulResults(response)) {
    const environment = environmentOf(result)
    const capability = capabilityOf(result)
    const value = vectorValue(result)
    if (environment !== snapshot.metrics.environment || !capability || value === null) continue
    const metrics = snapshot.metrics
    updateIdentity(metrics, result)
    metrics.capabilities[capability][field] = value
  }
}

function applyAlerts(snapshot: GpuDashboardSnapshot, response: PromQLResponse): void {
  snapshot.metrics.alerts = []
  for (const result of successfulResults(response)) {
    const environment = environmentOf(result)
    if (environment !== snapshot.metrics.environment) continue
    snapshot.metrics.alerts.push({
      name: result.metric['alertname'] || 'unknown',
      severity: result.metric['severity'] || 'unknown',
    })
    updateIdentity(snapshot.metrics, result)
  }
  snapshot.metrics.alerts.sort((a, b) => a.name.localeCompare(b.name))
}

function applyRange(
  snapshot: GpuDashboardSnapshot,
  response: PromQLResponse,
  field: 'framebufferSeries' | 'throughputSeries',
): void {
  snapshot.metrics[field] = []
  const points = new Map<number, number>()
  for (const result of successfulResults(response)) {
    const environment = environmentOf(result)
    if (environment !== snapshot.metrics.environment) continue
    for (const [timestamp, raw] of result.values ?? []) {
      const value = Number.parseFloat(raw)
      if (!Number.isFinite(value)) continue
      const time = timestamp * 1000
      points.set(time, Math.min(points.get(time) ?? Number.POSITIVE_INFINITY, value))
    }
  }
  snapshot.metrics[field] = [...points.entries()]
    .sort(([left], [right]) => left - right)
    .map(([time, value]) => ({ time, value }))
}

function applyInstantResult(snapshot: GpuDashboardSnapshot, key: InstantQueryKey, response: PromQLResponse): void {
  switch (key) {
    case 'targets': applyTargets(snapshot, response); break
    case 'freshness': applyFreshness(snapshot, response); break
    case 'gauges': applyGauges(snapshot, response); break
    case 'rejections': applyRejections(snapshot, response); break
    case 'recoveries': applyRecoveries(snapshot, response); break
    case 'capabilityThroughput': applyCapability(snapshot, response, 'throughput'); break
    case 'capabilityFailures': applyCapability(snapshot, response, 'failures'); break
    case 'capabilityLatency': applyCapability(snapshot, response, 'latencySeconds'); break
    case 'alerts': applyAlerts(snapshot, response); break
  }
}

function deriveCapabilityTotals(snapshot: GpuDashboardSnapshot): void {
  const metrics = snapshot.metrics
  for (const field of ['throughput', 'failures'] as const) {
    const values = GPU_CAPABILITIES
      .map(capability => metrics.capabilities[capability][field])
      .filter((value): value is number => value !== null)
    metrics[field] = values.length > 0 ? values.reduce((sum, value) => sum + value, 0) : null
  }
}

function queryWindow(now: Date): { start: string; end: string; step: string } {
  const end = Math.floor(now.getTime() / 1000)
  return { start: String(end - 6 * 60 * 60), end: String(end), step: '5m' }
}

export async function loadGpuDashboard(
  environment: GpuEnvironment,
  previous?: GpuDashboardSnapshot,
  now = new Date(),
  signal?: AbortSignal,
): Promise<GpuDashboardLoadResult> {
  const snapshot = cloneDashboard(environment, previous)
  const failedQueries: string[] = []
  const window = queryWindow(now)
  const instantEntries = Object.entries(gpuInstantQueries(environment)) as [InstantQueryKey, string][]
  const rangeEntries = Object.entries(gpuRangeQueries(environment)) as [RangeQueryKey, string][]
  type DashboardRequest =
    | { kind: 'instant'; key: InstantQueryKey; request: Promise<PromQLResponse> }
    | { kind: 'range'; key: RangeQueryKey; request: Promise<PromQLResponse> }
  const requests: DashboardRequest[] = [
    ...instantEntries.map(([key, query]) => ({
      kind: 'instant' as const,
      key,
      request: queryGpuInstant(query, signal),
    })),
    ...rangeEntries.map(([key, query]) => ({
      kind: 'range' as const,
      key,
      request: queryGpuRange(query, window.start, window.end, window.step, signal),
    })),
  ]
  const settled = await Promise.allSettled(requests.map(request => request.request))

  settled.forEach((result, index) => {
    const request = requests[index]
    if (!request) return
    if (result.status === 'rejected') {
      failedQueries.push(`${request.kind}:${request.key}`)
      return
    }
    try {
      if (request.kind === 'instant') {
        applyInstantResult(snapshot, request.key, result.value)
      } else {
        applyRange(snapshot, result.value, request.key === 'framebuffer' ? 'framebufferSeries' : 'throughputSeries')
      }
    } catch {
      failedQueries.push(`${request.kind}:${request.key}`)
    }
  })

  deriveCapabilityTotals(snapshot)

  snapshot.refreshedAt = now.getTime()
  return { snapshot, failedQueries }
}
